import React, { useState, useMemo, useEffect } from "react";
import { useLocation, useNavigate } from "@tanstack/react-router";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../@/components/ui/tabs";
import { Button } from "../../@/components/ui/button";
import { Checkbox } from "../../@/components/ui/checkbox";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../@/components/ui/dropdown-menu";
import { MoreVertical, Plus } from "lucide-react";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../@/components/ui/select";
import MainSidebar from "../shared/MainSidebar";
import DefaultTable from "../components/defaultTable";
import Pagination from "../components/pagination";
import AddCategoryDialog from "../forms/addCategory";
import AddSkillDialog from "../forms/addSkill";
import axios from "axios";
import { api_endpoint } from "../constants/constant";
import { useAuth } from "@clerk/clerk-react";
import { Spinner } from "../components/spinner";
import UpdateCategoryDialog from "../forms/editCategory";
import DeleteCategoryDialog from "../forms/deleteCategory";
import DeleteCategoriesBulkDialog from "../forms/deleteCategoriesBulk";
import EditSkillDialog from "../forms/editSkill";
import DeleteSkillDialog from "../forms/deleteSkill";
import DeleteSkillsBulkDialog from "../forms/deleteSkillsBulk";
import TableToolbar from "../components/tableToolbar";
import LessonExplorerBadges from "../components/badgeCard";
import { fetchAllPaginatedItems } from "@/lib/fetchAllPaginatedItems";
import { toast } from "sonner";

function buildHierarchicalOrder(rows, { idKey, parentKey, sortKey }) {
  const safeRows = Array.isArray(rows) ? rows : [];
  const byId = new Map(safeRows.map((row) => [row?.[idKey], row]));

  const childrenByParent = new Map();
  for (const row of safeRows) {
    const parentId = row?.[parentKey] ?? null;
    if (!childrenByParent.has(parentId)) childrenByParent.set(parentId, []);
    childrenByParent.get(parentId).push(row);
  }

  const compare = (a, b) => {
    if (sortKey === "items") {
      return (b?.items || 0) - (a?.items || 0);
    }
    // default: name
    return String(a?.name || "").localeCompare(String(b?.name || ""), undefined, {
      sensitivity: "base",
    });
  };

  for (const [, kids] of childrenByParent) {
    kids.sort(compare);
  }

  const roots = [];
  for (const row of safeRows) {
    const parentId = row?.[parentKey] ?? null;
    const hasValidParent = parentId != null && byId.has(parentId);
    if (!hasValidParent) roots.push(row);
  }
  roots.sort(compare);

  const ordered = [];
  const visited = new Set();

  const dfs = (node) => {
    const nodeId = node?.[idKey];
    if (nodeId == null) return;
    if (visited.has(nodeId)) return;
    visited.add(nodeId);

    ordered.push(node);
    const kids = childrenByParent.get(nodeId) || [];
    for (const kid of kids) dfs(kid);
  };

  for (const root of roots) dfs(root);

  // Include any stragglers (e.g., cycles or missing parents) deterministically.
  for (const row of safeRows) {
    const rowId = row?.[idKey];
    if (rowId != null && !visited.has(rowId)) dfs(row);
  }

  return ordered;
}

export default function GlobalTaxonomies() {
  const location = useLocation();
  const navigate = useNavigate();
  const urlTab =
    typeof window !== "undefined"
      ? new URLSearchParams(location.search).get("tab")
      : null;
  const initialTab =
    (location.state && location.state.tab) || urlTab || "categories";
  const [activeTab, setActiveTab] = useState(initialTab);
  const [selectedItems, setSelectedItems] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [categoriesData, setCategoriesData] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [editCategory, setEditCategory] = useState(null);
  const [skillsData, setSkillsData] = useState([]);
  const [loadingSkills, setLoadingSkills] = useState(false);
  const { getToken } = useAuth();

  const [bulkDeleteCategoriesOpen, setBulkDeleteCategoriesOpen] = useState(false);
  const [bulkDeleteCategoryIds, setBulkDeleteCategoryIds] = useState([]);

  const [bulkDeleteSkillsOpen, setBulkDeleteSkillsOpen] = useState(false);
  const [bulkDeleteSkillIds, setBulkDeleteSkillIds] = useState([]);

  // state for search/filter/sort for categories and skills
  const [categorySearch, setCategorySearch] = useState("");
  const [categorySort, setCategorySort] = useState("name");
  const [pendingCategorySort, setPendingCategorySort] = useState("name");

  const [skillSearch, setSkillSearch] = useState("");
  const [skillSort, setSkillSort] = useState("name");
  const [pendingSkillSort, setPendingSkillSort] = useState("name");

  const [categoryLevelFilter, setCategoryLevelFilter] = useState("ALL");
  const [pendingCategoryLevelFilter, setPendingCategoryLevelFilter] =
    useState("ALL");
  const [skillLevelFilter, setSkillLevelFilter] = useState("ALL");
  const [pendingSkillLevelFilter, setPendingSkillLevelFilter] = useState("ALL");

  // Category sort options
  const categorySortOptions = [
    { value: "name", label: "Category Name" },
    { value: "items", label: "Items" },
  ];

  // Skill sort options
  const skillSortOptions = [
    { value: "name", label: "Skill Name" },
    { value: "items", label: "Items" },
  ];

  const fetchCategories = async () => {
    setLoadingCategories(true);
    try {
      const token = await getToken();
      if (!token) return;

      const reqconfig = {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      };

      const extractCategories = (payload) => {
        const value = payload?.categories ?? payload?.data?.categories ?? payload;
        if (Array.isArray(value)) return value;
        if (Array.isArray(value?.data)) return value.data;
        return [];
      };

      let rawCategories = [];
      try {
        rawCategories = await fetchAllPaginatedItems({
          url: api_endpoint + "/course/categories",
          token,
          headers: reqconfig.headers,
          mode: "page",
          pageSize: 200,
          maxPages: 200,
          selectItems: extractCategories,
          getItemKey: (item) => String(item?.id ?? item?._id ?? JSON.stringify(item)),
          dedupe: true,
        });
      } catch (e) {
        // Fallback: some endpoints may not accept pagination params yet.
        const res = await axios.get(api_endpoint + "/course/categories", reqconfig);
        rawCategories = extractCategories(res?.data);
      }

      console.log("Fetched categories:", { count: rawCategories?.length ?? 0 });
      const categoryMap = new Map(rawCategories.map((cat) => [cat.id, cat]));
      const levelMemo = new Map();

      const computeLevel = (cat) => {
        if (!cat?.parent_category_id) return 0;
        if (levelMemo.has(cat.id)) return levelMemo.get(cat.id);

        const parent = categoryMap.get(cat.parent_category_id);
        const level = parent ? computeLevel(parent) + 1 : 0;
        levelMemo.set(cat.id, level);
        return level;
      };

      const categoriesWithMetadata = rawCategories.map((cat) => ({
        ...cat,
        items: typeof cat.items === "number" ? cat.items : 0,
        level: computeLevel(cat),
      }));

      setCategoriesData(categoriesWithMetadata);
    } catch (error) {
      console.error("Error fetching categories:", error);
      setCategoriesData([]);
    } finally {
      setLoadingCategories(false);
    }
  };

  const fetchSkills = async () => {
    setLoadingSkills(true);
    try {
      const token = await getToken();
      if (!token) return;

      const reqconfig = {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      };
      const extractSkills = (payload) => {
        const value = payload?.skills ?? payload?.data?.skills ?? payload;
        if (Array.isArray(value)) return value;
        if (Array.isArray(value?.data)) return value.data;
        return [];
      };

      let rawSkills = [];
      try {
        rawSkills = await fetchAllPaginatedItems({
          url: api_endpoint + "/course/skills",
          token,
          headers: reqconfig.headers,
          mode: "page",
          pageSize: 200,
          maxPages: 200,
          selectItems: extractSkills,
          getItemKey: (item) => String(item?.id ?? item?._id ?? JSON.stringify(item)),
          dedupe: true,
        });
      } catch (e) {
        // Fallback: some endpoints may not accept pagination params yet.
        const res = await axios.get(api_endpoint + "/course/skills", reqconfig);
        rawSkills = extractSkills(res?.data);
      }

      console.log("Fetched skills:", { count: rawSkills?.length ?? 0 });
      const skillMap = new Map(rawSkills.map((skill) => [skill.id, skill]));
      const levelMemo = new Map();

      const computeLevel = (skill) => {
        if (!skill?.parent_skill_id) return 0;
        if (levelMemo.has(skill.id)) return levelMemo.get(skill.id);

        const parent = skillMap.get(skill.parent_skill_id);
        const level = parent ? computeLevel(parent) + 1 : 0;
        levelMemo.set(skill.id, level);
        return level;
      };

      const skillsWithMetadata = rawSkills.map((skill) => ({
        ...skill,
        items: typeof skill.items === "number" ? skill.items : 0,
        level: computeLevel(skill),
      }));

      setSkillsData(skillsWithMetadata);
    } catch (error) {
      console.error("Error fetching skills:", error);
      setSkillsData([]);
    } finally {
      setLoadingSkills(false);
    }
  };

  useEffect(() => {
    fetchCategories();
    fetchSkills();
  }, []);

  // Get prefix based on hierarchy level
  const getPrefix = (level) => {
    if (level === 0) return "";
    if (level === 1) return "— ";
    if (level === 2) return "— — ";
    return "— ".repeat(level);
  };

  // Selection handlers for tables
  const handleSelectItem = (id) => {
    setSelectedItems((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      return [...prev, id];
    });
  };

  const handleSelectAll = (data) => {
    if (!Array.isArray(data)) return;
    if (selectedItems.length === data.length) {
      setSelectedItems([]);
    } else {
      setSelectedItems(data.map((item) => item.id));
    }
  };

  const handleCategoryBulkAction = (label, ctx) => {
    const text = String(label ?? "").trim();
    const normalized = text.toLowerCase();
    if (!normalized.includes("delete")) return;

    const ids = Array.isArray(ctx?.selectedRowKeysOnPage)
      ? ctx.selectedRowKeysOnPage
      : [];

    if (ids.length < 2) {
      toast.message("Select at least 2 categories to bulk delete");
      return;
    }

    setBulkDeleteCategoryIds(ids);
    setBulkDeleteCategoriesOpen(true);
  };

  const handleSkillBulkAction = (label, ctx) => {
    const text = String(label ?? "").trim();
    const normalized = text.toLowerCase();
    if (!normalized.includes("delete")) return;

    const ids = Array.isArray(ctx?.selectedRowKeysOnPage)
      ? ctx.selectedRowKeysOnPage
      : [];

    if (ids.length < 2) {
      toast.message("Select at least 2 skills to bulk delete");
      return;
    }

    setBulkDeleteSkillIds(ids);
    setBulkDeleteSkillsOpen(true);
  };

  // Categories columns
  const categoryColumns = [
    {
      key: "checkbox",
      header: "",
    },
    {
      key: "name",
      header: "Category",
      render: (row) => (
        <span className="font-medium font-body">
          {getPrefix(row.level)}
          {row.name}
        </span>
      ),
    },
    {
      key: "description",
      header: "Description",
    },
    {
      key: "items",
      header: "Items",
    },
    {
      key: "actions",
      header: "",
      render: (row) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm">
              <MoreVertical className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem asChild>
              <UpdateCategoryDialog
                category={row}
                categories={categoriesData}
                onUpdated={fetchCategories}
                trigger={
                  <Button
                    variant="ghost"
                    size="sm"
                    className="w-full justify-start h-auto p-2 font-normal"
                  >
                    Edit
                  </Button>
                }
              />
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <DeleteCategoryDialog
                category={row}
                hasChildren={categoriesData.some(
                  (cat) => cat?.parent_category_id === row?.id
                )}
                onDeleted={fetchCategories}
                trigger={
                  <Button
                    variant="ghost"
                    size="sm"
                    className="w-full justify-start h-auto p-2 font-normal text-(--color-destructive)"
                  >
                    Delete
                  </Button>
                }
              />
            </DropdownMenuItem>
            {/* <DropdownMenuItem>Add Sub-category</DropdownMenuItem> */}
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];

  // Skills columns
  const skillColumns = [
    {
      key: "checkbox",
      header: "",
    },
    {
      key: "name",
      header: "Skill",
      render: (row) => (
        <span className="font-medium font-body">
          {getPrefix(row.level)}
          {row.name}
        </span>
      ),
    },
    {
      key: "description",
      header: "Description",
    },
    {
      key: "items",
      header: "Items",
    },
    {
      key: "actions",
      header: "",
      render: (row) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm">
              <MoreVertical className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem asChild>
              <EditSkillDialog
                skill={row}
                skills={skillsData}
                onUpdated={fetchSkills}
                trigger={
                  <Button
                    variant="ghost"
                    size="sm"
                    className="w-full justify-start h-auto p-2 font-normal"
                  >
                    Edit
                  </Button>
                }
              />
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <DeleteSkillDialog
                skill={row}
                hasChildren={skillsData.some(
                  (s) => s?.parent_skill_id === row?.id
                )}
                onDeleted={fetchSkills}
                trigger={
                  <Button
                    variant="ghost"
                    size="sm"
                    className="w-full justify-start h-auto p-2 font-normal text-(--color-destructive)"
                  >
                    Delete
                  </Button>
                }
              />
            </DropdownMenuItem>
            {/* <DropdownMenuItem>Add Sub-skill</DropdownMenuItem> */}
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];

  // Memoized filtered and sorted categories
  const filteredSortedCategories = useMemo(() => {
    let result = categoriesData;
    if (categorySearch.trim()) {
      const term = categorySearch.toLowerCase();
      result = result.filter(
        (cat) =>
          String(cat?.name || "").toLowerCase().includes(term) ||
          String(cat?.description || "").toLowerCase().includes(term)
      );
    }

    // Keep hierarchy: sort siblings, but always render children under their parent.
    return buildHierarchicalOrder(result, {
      idKey: "id",
      parentKey: "parent_category_id",
      sortKey: categorySort,
    });
  }, [categoriesData, categorySearch, categorySort]);

  // Memoized filtered and sorted skills
  const filteredSortedSkills = useMemo(() => {
    let result = skillsData;
    if (skillSearch.trim()) {
      const term = skillSearch.toLowerCase();
      result = result.filter(
        (skill) =>
          String(skill?.name || "").toLowerCase().includes(term) ||
          String(skill?.description || "").toLowerCase().includes(term)
      );
    }

    // Keep hierarchy: sort siblings, but always render children under their parent.
    return buildHierarchicalOrder(result, {
      idKey: "id",
      parentKey: "parent_skill_id",
      sortKey: skillSort,
    });
  }, [skillsData, skillSearch, skillSort]);

  // Paginate data
  const getPaginatedData = (data) => {
    const startIndex = (currentPage - 1) * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    return data.slice(startIndex, endIndex);
  };

  const paginatedCategories = getPaginatedData(filteredSortedCategories);
  const paginatedSkills = getPaginatedData(filteredSortedSkills);
  const totalCategoryPages =
    Math.ceil(filteredSortedCategories.length / rowsPerPage) || 1;
  const totalSkillPages =
    Math.ceil(filteredSortedSkills.length / rowsPerPage) || 1;

  return (
    <>
      <div className="flex flex-col w-full min-h-screen">
        <div className="w-full mx-auto">
          {/* Header */}
          <h1 className="">Global Taxonomies</h1>
          <p className="text-(--neutral-800)">
            Manage all global LMS taxonomies for seamless categorisation,
            search, and filters
          </p>

          {/* Tabs */}
          <Tabs
            value={activeTab}
            onValueChange={(value) => {
              setActiveTab(value);
              try {
                const params = new URLSearchParams(location.search);
                params.set("tab", value);
                navigate({
                  to: `${location.pathname}?${params.toString()}`,
                  replace: true,
                });
              } catch (e) {
                console.warn("Failed to update tab in URL", e);
              }
            }}
            className="w-full space-y-4 mt-3"
          >
            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <TabsList className="">
                <TabsTrigger
                  value="categories"
                  className="flex-1 md:flex-none w-full md:w-auto px-3 py-0.5"
                >
                  Categories
                </TabsTrigger>
                <TabsTrigger
                  value="skills"
                  className="flex-1 md:flex-none w-full md:w-auto px-3 py-0.5"
                >
                  Skills
                </TabsTrigger>
              </TabsList>

              <div className="w-full md:w-auto">
                {activeTab === "categories" ? (
                  <AddCategoryDialog
                    categories={categoriesData}
                    onAdded={fetchCategories}
                  />
                ) : (
                  <AddSkillDialog skills={skillsData} onAdded={fetchSkills} />
                )}
              </div>
            </div>

            {/* Categories Tab */}
            <TabsContent value="categories" className="space-y-0">
              <TableToolbar
                searchTerm={categorySearch}
                onSearchChange={setCategorySearch}
                filters={[]}
                sorts={[
                  {
                    label: "Sort by",
                    value: pendingCategorySort,
                    onChange: setPendingCategorySort,
                    onClear: () => setPendingCategorySort("name"),
                    options: categorySortOptions,
                  },
                ]}
                onApplySort={() => setCategorySort(pendingCategorySort)}
              />
              {loadingCategories ? (
                <Spinner />
              ) : (
                <>
                  <DefaultTable
                    columns={categoryColumns}
                    data={paginatedCategories}
                    emptyMessage="No categories found."
                    selectedItems={selectedItems}
                    onSelectItem={handleSelectItem}
                    onSelectAll={() =>
                      handleSelectAll(filteredSortedCategories)
                    }
                    bulkActions={["Edit", "Delete"]}
                    onBulkAction={handleCategoryBulkAction}
                  />
                  <DeleteCategoriesBulkDialog
                    open={bulkDeleteCategoriesOpen}
                    onOpenChange={setBulkDeleteCategoriesOpen}
                    categoryIds={bulkDeleteCategoryIds}
                    categories={categoriesData}
                    onDeleted={() => {
                      setSelectedItems([]);
                      setBulkDeleteCategoryIds([]);
                      fetchCategories();
                    }}
                  />
                  <Pagination
                    totalPages={totalCategoryPages}
                    currentPage={currentPage}
                    rowsPerPage={rowsPerPage}
                    totalRows={filteredSortedCategories.length}
                    onPageChange={setCurrentPage}
                    onRowsPerPageChange={(rows) => {
                      setRowsPerPage(rows);
                      setCurrentPage(1);
                    }}
                    selectedCount={selectedItems.length}
                  />
                </>
              )}
            </TabsContent>

            {/* Skills Tab */}
            <TabsContent value="skills" className="space-y-0">
              <TableToolbar
                searchTerm={skillSearch}
                onSearchChange={setSkillSearch}
                filters={[]}
                sorts={[
                  {
                    label: "Sort by",
                    value: pendingSkillSort,
                    onChange: setPendingSkillSort,
                    onClear: () => setPendingSkillSort("name"),
                    options: skillSortOptions,
                  },
                ]}
                onApplySort={() => setSkillSort(pendingSkillSort)}
              />
              {loadingSkills ? (
                <Spinner />
              ) : (
                <>
                  <DefaultTable
                    columns={skillColumns}
                    data={paginatedSkills}
                    emptyMessage="No skills found."
                    selectedItems={selectedItems}
                    onSelectItem={handleSelectItem}
                    onSelectAll={() => handleSelectAll(filteredSortedSkills)}
                    bulkActions={["Edit", "Delete"]}
                    onBulkAction={handleSkillBulkAction}
                  />
                  <DeleteSkillsBulkDialog
                    open={bulkDeleteSkillsOpen}
                    onOpenChange={setBulkDeleteSkillsOpen}
                    skillIds={bulkDeleteSkillIds}
                    skills={skillsData}
                    onDeleted={() => {
                      setSelectedItems([]);
                      setBulkDeleteSkillIds([]);
                      fetchSkills();
                    }}
                  />
                  <Pagination
                    totalPages={totalSkillPages}
                    currentPage={currentPage}
                    rowsPerPage={rowsPerPage}
                    totalRows={filteredSortedSkills.length}
                    onPageChange={setCurrentPage}
                    onRowsPerPageChange={(rows) => {
                      setRowsPerPage(rows);
                      setCurrentPage(1);
                    }}
                    selectedCount={selectedItems.length}
                  />
                </>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </>
  );
}
